import { useCallback } from 'react';
import { RentVerificationStatus } from 'src/api/rentVerificationApi';
import FolderRowLoader from 'src/components/molecules/FolderRowLoader';
import { ShowState } from 'src/components/molecules/ShowState';
import { calculateProcessingTime } from 'src/components/organisms/RentVerificationRow/utils';
import RentVerificationTable from 'src/components/organisms/RentVerificationTable';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { useGetRentVerification, useRentVerificationInfo } from 'src/features/rentVerification';
import { dateFormatWithMonthNameAndTimeLocal } from 'src/helpers';
import { useDataFetch } from 'src/hooks/useDataFetch';

// UI data structure (matches what the table component expects)
export interface RentVerificationTableItem {
  id: string;
  propertyName: string;
  applicantName: string;
  reportUrl: string;
  status: string;
  applicantId: string;
  insertedAt: string;
  processingTime: string;
}

const RentVerificationList: React.FC = () => {
  const { role } = useUserRoles();
  const { onGetRentVerifications } = useGetRentVerification();
  const { data, filters, isLoading, isError, isUninitialized } = useRentVerificationInfo();

  const fetchData = useCallback(
    () => onGetRentVerifications(filters),
    [onGetRentVerifications, filters]
  );
  useDataFetch(fetchData, filters, isUninitialized);

  if (isError) {
    return (
      <ShowState
        variant="error"
        type="information"
        buttonLabel="Please try again"
        message="Something went wrong"
        onClick={() => window.location.reload()}
      />
    );
  }

  if (isLoading) {
    return <FolderRowLoader />;
  }

  if (!role) return null;

  if (data?.length === 0) {
    return (
      <ShowState
        message="There are no rent verifications <br /> available to show"
        type="application"
        variant="empty"
      />
    );
  }

  if (!data) {
    return (
      <ShowState
        message="There are no rent verifications <br /> available to show"
        type="application"
        variant="empty"
      />
    );
  }

  // Transform API response to UI data structure
  const transformedData = data.map((verification) => ({
    id: verification.id,
    propertyName: verification.property_name || 'Unknown Property',
    applicantName: verification.applicant_full_name || 'Unknown Applicant',
    reportUrl: verification.report_url || '',
    status: verification.status || 'Unknown Status',
    applicantId: verification.applicant_id || '',
    insertedAt: verification.inserted_at
      ? dateFormatWithMonthNameAndTimeLocal(verification.inserted_at)
      : 'N/A',
    processingTime: calculateProcessingTime(
      verification.status as RentVerificationStatus,
      verification.inserted_at,
      verification.updated_at
    )
  }));

  return <RentVerificationTable verifications={transformedData} role={role} />;
};

export default RentVerificationList;
